import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Tooltip,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  FormControlLabel,
  Divider,
  Tabs,
  Tab,
  TextField,
  MenuItem,
  Button,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  Engineering as EngineeringIcon,
  Speed as SpeedIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Timer as TimerIcon,
  LinkOff as LinkOffIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

// Bionen status mapping
const BIONEN_STATUS = {
  1: { label: 'STOP', color: 'warning', description: 'Macchina in STOP' },
  2: { label: 'RESET', color: 'info', description: 'Macchina in RESET' },
  3: { label: 'START', color: 'success', description: 'Macchina in START' }
};

// Bionen reject types
const REJECT_TYPES = {
  0: 'Mancanza ago (ST3)',
  1: 'Diametro non corretto (ST3)',
  2: 'Lunghezza ago non corretta (ST3)',
  3: 'Quota verifica non raggiungibile (ST3)',
  4: 'Verifica taglio fallita (ST3)',
  5: 'Mancanza ago (ST2)'
};

// Category display config
const CATEGORY_CONFIG = {
  order: { label: 'Dati Ordine', color: '#0693e3', icon: '>' },
  production: { label: 'Lavorazione', color: '#10b981', icon: '>' },
  production_summary: { label: 'Fine Lavorazione', color: '#f59e0b', icon: '>' },
  custom: { label: 'Stato / PLC', color: '#6b7280', icon: '>' },
};

const MachineDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [resetDialog, setResetDialog] = useState(false);
  const [linkSnackbar, setLinkSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [machine, setMachine] = useState(null);
  const [values, setValues] = useState({});
  const [nodes, setNodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [staleInfo, setStaleInfo] = useState({ isStale: false, lastLiveDataAt: null });
  const [activeTab, setActiveTab] = useState(0);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyHours, setHistoryHours] = useState(24);
  const [historyNodeFilter, setHistoryNodeFilter] = useState('');

  const loadMachine = useCallback(async () => {
    try {
      const res = await axios.get(`/api/machines/${id}`);
      setMachine(res.data);
    } catch (err) {
      setError('Errore nel caricamento macchina');
    }
  }, [id]);

  const loadNodes = useCallback(async () => {
    try {
      const res = await axios.get(`/api/machines/${id}/opcua-nodes`);
      setNodes(res.data);
    } catch (err) {
      console.error('Error loading nodes:', err);
    }
  }, [id]);

  const loadValues = useCallback(async () => {
    try {
      const res = await axios.post(`/api/machines/${id}/opcua-nodes/read-values`);
      setValues(res.data.values || {});
      setStaleInfo({ isStale: !!res.data.isStale, lastLiveDataAt: res.data.lastLiveDataAt || null });
      setLastUpdate(new Date());
      setError(null);
    } catch (err) {
      if (err.response?.status === 502) {
        setError('Gateway non raggiungibile');
      } else {
        setError('Errore nella lettura valori');
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadMachine();
    loadNodes();
    loadValues();
  }, [loadMachine, loadNodes, loadValues]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(loadValues, 5000);
    return () => clearInterval(interval);
  }, [autoRefresh, loadValues]);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      let url = `/api/machines/${id}/value-history?hours=${historyHours}&limit=2000`;
      if (historyNodeFilter) url += `&node_name=${encodeURIComponent(historyNodeFilter)}`;
      const res = await axios.get(url);
      setHistory(res.data);
    } catch (err) {
      console.error('Error loading history:', err);
    } finally {
      setHistoryLoading(false);
    }
  }, [id, historyHours, historyNodeFilter]);

  useEffect(() => {
    if (activeTab === 1) loadHistory();
  }, [activeTab, loadHistory]);

  // Extract key values by node name
  const getVal = (name) => {
    const entry = Object.entries(values).find(([, v]) => v.name === name);
    return entry ? entry[1].value : null;
  };

  const statoMacchina = getVal('StatoMacchina');
  const pezziBuoni = getVal('PezziBuoni');
  const qt = getVal('Qt');
  const tempoCiclo = getVal('TempoCiclo');
  const produzioneInCorso = getVal('ProduzioneInCorso');
  const datiOrdinePronti = getVal('DatiOrdinePronti');
  const datiLavPronti = getVal('DatiLavorazionePronti');

  const statusInfo = BIONEN_STATUS[statoMacchina] || { label: `ERR (${statoMacchina})`, color: 'error', description: 'Errore' };

  // Group values by category
  const grouped = {};
  for (const [nodeId, val] of Object.entries(values)) {
    const cat = val.category || 'custom';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push({ nodeId, ...val });
  }
  // Sort each group by name
  for (const cat of Object.keys(grouped)) {
    grouped[cat].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }

  // Format value for display
  const formatValue = (val, name) => {
    if (val === null || val === undefined) return '-';
    if (typeof val === 'boolean') return val ? 'TRUE' : 'FALSE';
    if (Array.isArray(val)) {
      // AllarmiAttivi: count active alarms
      const active = val.filter(v => v === true).length;
      return active > 0 ? `${active} allarmi attivi` : 'Nessun allarme';
    }
    // Scarti with 0xFFFFFFFF = not used
    if (typeof val === 'number' && val === 4294967295) return 'N/A';
    // Large garbage values from PLC
    if (typeof val === 'number' && val > 100000000 && (name || '').toLowerCase().includes('scarti')) return 'N/A';
    return String(val);
  };

  // Get active alarms from AllarmiAttivi array
  const allarmiValue = getVal('AllarmiAttivi');
  const activeAlarms = [];
  if (Array.isArray(allarmiValue)) {
    // Bionen alarm descriptions
    const ALARM_DESC = {
      0: 'Protezioni', 1: 'Emergenza', 2: 'Mancanza Aria', 3: 'Ricetta Non Caricata',
      4: 'Errore Parametri', 8: 'Stop Contapezzi', 9: 'Mancanza Aghi',
      13: 'Zero Asse X', 14: 'Conflitto Asse Y', 16: 'Azionamento Asse X',
      17: 'Extracorsa Ind. X', 18: 'Extracorsa Av. X', 19: 'Inseguimento X',
      20: 'Finestra Pos. X', 21: 'Offset Dac X', 24: 'Pinza Sfogliatore',
      25: 'Movimento Sfogliatore', 26: 'Inverter Martellatrice',
      27: 'Sollevamento Rotazione Ago', 28: 'Rotazione Ago',
      32: 'Azionamento Asse Y', 33: 'Extracorsa Ind. Y', 34: 'Extracorsa Av. Y',
      35: 'Inseguimento Y', 36: 'Finestra Pos. Y', 37: 'Offset Dac Y',
      40: 'Pinza Di Scambio', 41: 'Zero Asse Y', 42: 'Conflitto Asse Z',
      43: 'Conflitto Asse X', 48: 'Azionamento Asse Z', 49: 'Extracorsa Ind. Z',
      50: 'Extracorsa Av. Z', 51: 'Inseguimento Z', 52: 'Finestra Pos. Z',
      53: 'Offset Dac Z', 56: 'Movimento Taglierina', 57: 'Zero Asse Z'
    };
    allarmiValue.forEach((v, i) => {
      if (v === true) activeAlarms.push({ bit: i, desc: ALARM_DESC[i] || `Allarme ${i}` });
    });
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const handleResetLink = async () => {
    try {
      const res = await axios.post(`/api/machines/${id}/reset-link`);
      setLinkSnackbar({ open: true, message: res.data?.message || 'Sblocco gateway richiesto', severity: 'success' });
    } catch (err) {
      setLinkSnackbar({ open: true, message: err.response?.data?.error || 'Errore nello sblocco del gateway', severity: 'error' });
    }
    setResetDialog(false);
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <IconButton onClick={() => navigate('/dashboard')}>
          <ArrowBackIcon />
        </IconButton>
        <EngineeringIcon sx={{ fontSize: 32, color: 'primary.main' }} />
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5">
            {machine?.name || `Macchina ${id}`}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {machine?.code} - {machine?.location || ''}
          </Typography>
        </Box>
        <FormControlLabel
          control={<Switch checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />}
          label="Auto-refresh 5s"
        />
        <Tooltip title="Aggiorna ora">
          <IconButton onClick={loadValues} color="primary">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
        {isAdmin() && (
          <Tooltip title="Riporta il protocollo del gateway a IDLE se un ordine e' rimasto appeso sulla macchina">
            <Button
              startIcon={<LinkOffIcon />}
              variant="outlined"
              color="error"
              size="small"
              onClick={() => setResetDialog(true)}
            >
              Sblocca Gateway
            </Button>
          </Tooltip>
        )}
        {lastUpdate && (
          <Typography variant="caption" color="text.secondary">
            {lastUpdate.toLocaleTimeString('it-IT')}
          </Typography>
        )}
      </Box>

      {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Stale data warning: il link OPC UA con la macchina e' fermo, i valori mostrati sono vecchi */}
      {staleInfo.isStale && !error && (
        <Alert severity="warning" sx={{ mb: 2 }} icon={<WarningIcon />}>
          <strong>Dati non aggiornati.</strong> Il collegamento OPC UA con la macchina e' assente o interrotto:
          i valori mostrati sono l'ultimo stato noto
          {staleInfo.lastLiveDataAt ? ` (aggiornato il ${new Date(staleInfo.lastLiveDataAt).toLocaleString('it-IT')})` : ''}.
          La macchina potrebbe essere spenta o non raggiungibile.
        </Alert>
      )}

      {/* Status Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">Stato Macchina</Typography>
              <Box sx={{ mt: 1 }}>
                <Chip
                  label={statusInfo.label}
                  color={statusInfo.color}
                  icon={statusInfo.color === 'success' ? <CheckCircleIcon /> : <ErrorIcon />}
                />
              </Box>
              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                {statusInfo.description}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">Pezzi Buoni / Richiesti</Typography>
              <Typography variant="h4" sx={{ mt: 1, fontWeight: 'bold', color: 'secondary.main' }}>
                {pezziBuoni ?? '-'} / {qt ?? '-'}
              </Typography>
              {qt > 0 && pezziBuoni !== null && (
                <Typography variant="caption" color="text.secondary">
                  {Math.round((pezziBuoni / qt) * 100)}% completato
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">Tempo Ciclo</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 1 }}>
                <TimerIcon sx={{ mr: 0.5, color: 'text.secondary' }} />
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  {tempoCiclo ?? '-'}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">ms</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">Produzione</Typography>
              <Box sx={{ mt: 1 }}>
                <Chip
                  label={produzioneInCorso ? 'In Corso' : 'Ferma'}
                  color={produzioneInCorso ? 'success' : 'default'}
                  size="small"
                />
              </Box>
              <Box sx={{ mt: 1, display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                <Chip
                  label={datiOrdinePronti ? 'Ordine pronto' : 'Nessun ordine'}
                  size="small"
                  variant="outlined"
                  color={datiOrdinePronti ? 'info' : 'default'}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs: Live / Storico */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
          <Tab label="Dati Live" />
          <Tab label="Storico" />
        </Tabs>
      </Paper>

      {activeTab === 0 && (<>
      {/* Active Alarms */}
      {activeAlarms.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }} icon={<WarningIcon />}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
            Allarmi Attivi ({activeAlarms.length})
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {activeAlarms.map((a) => (
              <Chip key={a.bit} label={a.desc} color="error" size="small" variant="outlined" />
            ))}
          </Box>
        </Alert>
      )}

      {/* Consuntivo pendente */}
      {datiLavPronti && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Consuntivo fine lavorazione disponibile (GUID: {getVal('FineLavorazione_GUID') || '?'})
        </Alert>
      )}

      {/* Node Values by Category */}
      {['order', 'production', 'production_summary', 'custom'].map((cat) => {
        const catNodes = grouped[cat];
        if (!catNodes || catNodes.length === 0) return null;
        const config = CATEGORY_CONFIG[cat] || { label: cat, color: '#999' };

        return (
          <Paper key={cat} sx={{ mb: 2 }}>
            <Box sx={{ p: 1.5, backgroundColor: config.color + '10', borderBottom: `2px solid ${config.color}` }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: config.color }}>
                {config.label} ({catNodes.length} nodi)
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', width: '35%' }}>Nome</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '45%' }}>Valore</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '20%' }}>Ultimo Aggiornamento</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {catNodes.map((node) => {
                    const displayVal = formatValue(node.value, node.name);
                    const isBoolean = typeof node.value === 'boolean';
                    const isTrue = node.value === true;

                    return (
                      <TableRow key={node.nodeId} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {node.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            {node.nodeId}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {isBoolean ? (
                            <Chip
                              label={displayVal}
                              size="small"
                              color={isTrue ? 'success' : 'default'}
                              variant={isTrue ? 'filled' : 'outlined'}
                            />
                          ) : (
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: displayVal !== '-' && displayVal !== 'N/A' ? 600 : 400 }}>
                              {displayVal}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {node.timestamp ? new Date(node.timestamp).toLocaleTimeString('it-IT') : '-'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        );
      })}
      </>)}

      {/* Tab Storico */}
      {activeTab === 1 && (
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
            <TextField
              select
              label="Periodo"
              value={historyHours}
              onChange={(e) => setHistoryHours(e.target.value)}
              size="small"
              sx={{ minWidth: 140 }}
            >
              <MenuItem value={1}>Ultima ora</MenuItem>
              <MenuItem value={6}>Ultime 6 ore</MenuItem>
              <MenuItem value={24}>Ultime 24 ore</MenuItem>
              <MenuItem value={48}>Ultime 48 ore</MenuItem>
              <MenuItem value={168}>Ultima settimana</MenuItem>
            </TextField>
            <TextField
              select
              label="Nodo"
              value={historyNodeFilter}
              onChange={(e) => setHistoryNodeFilter(e.target.value)}
              size="small"
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">Tutti i nodi</MenuItem>
              {[...new Set(Object.values(values).map(v => v.name))].sort().map(name => (
                <MenuItem key={name} value={name}>{name}</MenuItem>
              ))}
            </TextField>
            <IconButton onClick={loadHistory} color="primary">
              <RefreshIcon />
            </IconButton>
            <Typography variant="caption" color="text.secondary">
              {history.length} record
            </Typography>
          </Box>

          {historyLoading ? (
            <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
          ) : (
            <TableContainer sx={{ maxHeight: 500 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Data/Ora</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Nodo</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Categoria</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Valore</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.map((row, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {new Date(row.recorded_at).toLocaleString('it-IT')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {row.node_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={row.category} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {row.value}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                  {history.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography color="text.secondary">Nessun dato storico disponibile</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}
      {/* Dialog conferma sblocco gateway */}
      <Dialog open={resetDialog} onClose={() => setResetDialog(false)}>
        <DialogTitle>Sbloccare il gateway verso la macchina?</DialogTitle>
        <DialogContent>
          <Typography>
            Il protocollo Bionen del gateway per {machine?.name || `macchina ${id}`} verra' riportato a
            IDLE, liberando un eventuale ordine rimasto appeso che blocca il sistema.
          </Typography>
          <Typography sx={{ mt: 1 }} color="text.secondary" variant="body2">
            ATTENZIONE: se la macchina sta davvero lavorando un ordine, il lavoro prosegue e va
            fermato da HMI. Questo comando sblocca solo il tracciamento del gateway.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialog(false)}>Annulla</Button>
          <Button variant="contained" color="error" onClick={handleResetLink}>Sblocca</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={linkSnackbar.open}
        autoHideDuration={5000}
        onClose={() => setLinkSnackbar({ ...linkSnackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={linkSnackbar.severity} onClose={() => setLinkSnackbar({ ...linkSnackbar, open: false })}>
          {linkSnackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MachineDetail;
